import { type Account, AccountsService } from "@openmoney/proto/openmoney/accounts/v1";

import { useMutation, useQuery } from "@connectrpc/connect-query";
import { useState } from "react";

const formatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

function AccountRow({ account, onDeleted }: { account: Account; onDeleted: () => void }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(account.name);
  const [balance, setBalance] = useState(String(account.balance));

  const update = useMutation(AccountsService.method.update, {
    mutationKey: ["connect-query"],
    onSuccess: () => {
      setEditing(false);
    },
  });

  const del = useMutation(AccountsService.method.delete, {
    mutationKey: ["connect-query"],
    onSuccess: () => {
      onDeleted();
    },
  });

  if (editing) {
    return (
      <tr className="bg-[--bg-soft]">
        <td className="font-mono text-xs opacity-50">{account.id.slice(0, 8)}…</td>
        <td>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full py-1.5 px-2 text-sm"
          />
        </td>
        <td>
          <input
            type="number"
            step="0.01"
            value={balance}
            onChange={(e) => setBalance(e.target.value)}
            className="w-full py-1.5 px-2 text-sm"
          />
        </td>
        <td>
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => update.mutate({ id: account.id, name, balance: Number(balance) })}
              className="primary py-1.5 px-2.5 text-xs"
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="py-1.5 px-2.5 text-xs"
            >
              Cancel
            </button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td className="font-mono text-xs opacity-50">{account.id.slice(0, 8)}…</td>
      <td className="font-medium text-sm">{account.name}</td>
      <td className="money text-[--green] text-sm">
        {account.m
          ? formatter.format(Number(account.m.units))
          : `$${Number(account.balance).toFixed(2)}`}
      </td>
      <td>
        <div className="flex gap-1.5">
          <button type="button" onClick={() => setEditing(true)} className="py-1.5 px-2.5 text-xs">
            Edit
          </button>
          <button
            type="button"
            onClick={() => del.mutate({ id: account.id })}
            className="py-1.5 px-2.5 text-xs text-[--ink-muted]"
          >
            Delete
          </button>
        </div>
      </td>
    </tr>
  );
}

export function AccountList() {
  const { data, isLoading, error } = useQuery(AccountsService.method.list);
  const [deleted, setDeleted] = useState(0);

  if (isLoading) return <p className="text-[--ink-muted] text-sm m-0">Loading accounts…</p>;
  if (error) return <p className="text-[--green] m-0 text-sm">Error: {error.message}</p>;

  if (!data?.accounts || data.accounts.length === 0) {
    return (
      <div className="text-center py-8 px-4 text-[--ink-muted] text-sm">
        No accounts yet. Create one to get started.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-separate border-spacing-0">
        <thead>
          <tr className="border-b border-[--line]">
            <th className="py-2 px-2.5 text-left font-semibold text-xs uppercase tracking-wide text-[--ink-muted] font-mono">
              ID
            </th>
            <th className="py-2 px-2.5 text-left font-semibold text-xs uppercase tracking-wide text-[--ink-muted] font-mono">
              Name
            </th>
            <th className="py-2 px-2.5 text-left font-semibold text-xs uppercase tracking-wide text-[--ink-muted] font-mono">
              Balance
            </th>
            <th className="py-2 px-2.5 text-left font-semibold text-xs uppercase tracking-wide text-[--ink-muted] font-mono">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="[&_tr]:border-b [&_tr]:border-[--line-soft] [&_tr]:transition-colors [&_tr]:duration-150 [&_tr:hover]:bg-[--bg-soft] [&_td]:py-[0.55rem] [&_td]:px-2.5">
          {data.accounts.map((account) => (
            <AccountRow
              key={account.id + deleted}
              account={account}
              onDeleted={() => setDeleted((n) => n + 1)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
